"use client"

import React, { useEffect, useState } from 'react'
import { PartnerRegionAssociation } from '../types/partner.type'
import { Button } from '@/shared/components/ui/button'
import { Edit, Trash2, Phone, Mail } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Switch } from '@/shared/components/ui/switch'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionAwareHref } from '@/shared/lib/utils/region-url.utils'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'

function extractAssociations(raw: unknown): PartnerRegionAssociation[] {
  if (Array.isArray(raw)) {
    return raw as PartnerRegionAssociation[]
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (obj.data !== undefined) {
      return extractAssociations(obj.data)
    }
  }
  return []
}

// Helper function to extract domain name from URL
function getDomainName(url: string): string {
  if (!url) return url
  try {
    // Remove protocol (http:// or https://)
    let domain = url.replace(/^https?:\/\//, '')
    // Remove www. if present
    domain = domain.replace(/^www\./, '')
    // Remove path and query string (everything after the first /)
    domain = domain.split('/')[0]
    return domain
  } catch {
    return url
  }
}

// Status Toggle Switch Component
function StatusToggleSwitch({
  associationId,
  currentStatus,
  onStatusChange,
}: {
  associationId: number
  currentStatus: boolean
  onStatusChange?: (associationId: number, newStatus: boolean) => void
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [localStatus, setLocalStatus] = useState(currentStatus)
  const [isOptimistic, setIsOptimistic] = useState(false)

  // Update local status when currentStatus prop changes, but only if not in optimistic state
  useEffect(() => {
    if (!isOptimistic) {
      setLocalStatus(currentStatus)
    }
  }, [currentStatus, isOptimistic])

  const handleToggle = async (checked: boolean) => {
    if (!onStatusChange) return

    setIsLoading(true)

    // Optimistically update the local state
    setLocalStatus(checked)
    setIsOptimistic(true)

    try {
      await onStatusChange(associationId, checked)
      // Keep the optimistic state - don't revert
    } catch (error) {
      console.error("Failed to update partner status:", error)
      // Revert the local state on error
      setLocalStatus(currentStatus)
      setIsOptimistic(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Switch
      checked={localStatus}
      onCheckedChange={handleToggle}
      disabled={isLoading}
      style={{ "--switch-checked-bg": "#81a2b6" } as React.CSSProperties}
      className="data-[state=checked]:bg-[#81a2b6]"
    />
  )
}

export default function PartnersList() {
  const [associations, setAssociations] = useState<PartnerRegionAssociation[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const router = useRouter()
  const { regionId, regionSlug, isLoading: regionLoading } = useRegionId()
  const { session } = useSession()
  const searchParams = useSearchParams()
  const searchQuery = searchParams.get("search") || ""
  
  // Only ADMIN and BASE_LEADER can edit/delete partners
  const canManagePartners = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  // Volunteers, Supporters, and Homeowners see only Name, Website, Giving Link
  const isLimitedView =
    session?.role === UserRole.VOLUNTEER ||
    session?.role === UserRole.Supporter ||
    session?.role === UserRole.HOMEOWNER

  useEffect(() => {
    if (!regionLoading && regionId) {
      fetchPartners(regionId)
    }
  }, [regionId, regionLoading])

  const fetchPartners = async (activeRegionId: number) => {
    setLoading(true)
    try {
      const res = await authFetchJson<any>(`/organization-regions?regionId=${activeRegionId}`)
      if (res.success && res.data) {
        const data = extractAssociations(res)
        setAssociations(data)
      } else {
        setAssociations([])
      }
    } catch (error) {
      console.error('Error fetching partners:', error)
      setAssociations([])
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (organizationId: number) => {
    const href = buildRegionAwareHref(regionSlug, `/partners/${organizationId}/edit`)
    router.push(href)
  }

  const handleDelete = async (associationId: number) => {
    if (!confirm('Are you sure you want to remove this partner from this region?')) {
      return
    }

    setDeletingId(associationId)
    try {
      const response = await authFetch(`/organization-regions/${associationId}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        successToast('Partner removed from region')
        if (regionId) {
          await fetchPartners(regionId)
        }
      } else {
        const error = await response.json().catch(() => ({}))
        errorToast(error?.error ?? 'Unable to remove partner. Please try again.')
      }
    } catch (error) {
      console.error('Error deleting partner:', error)
      errorToast('Unable to remove partner. Please try again later.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (associationId: number, newStatus: boolean) => {
    try {
      const response = await authFetch(`/organization-regions/${associationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: newStatus }),
      })

      if (response.ok && regionId) {
        await fetchPartners(regionId)
      } else {
        throw new Error('Failed to update partner status')
      }
    } catch (error) {
      console.error('Error updating partner:', error)
      throw error
    }
  }

  // Filter associations based on search query - MUST be before any early returns
  const filteredAssociations = useMemo(() => {
    if (!searchQuery.trim()) {
      return associations
    }

    const searchLower = searchQuery.toLowerCase()
    return associations.filter((association) => {
      const partner = association.organization
      const searchableText = [
        partner.name,
        partner.contactPerson,
        partner.phoneNumber,
        partner.email,
        partner.address,
        partner.city,
        partner.state,
        partner.zipCode,
        partner.specialties,
        partner.websiteUrl,
        partner.giveLink,
        partner.generalNotes,
        association.contactPerson,
        association.contactPhone,
        association.contactEmail,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(searchLower)
    })
  }, [associations, searchQuery])

  if (regionLoading || loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-10 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!associations.length && !loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground text-lg">No partners found</p>
          <p className="text-muted-foreground text-sm mt-2">
            Get started by adding your first partner organization for this region
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!filteredAssociations.length && associations.length > 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground text-lg">No partners found matching your search</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {filteredAssociations.map((association) => {
        const partner = association.organization
        return (
          <Card key={association.id}>
            <CardContent className="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{partner.name}</h3>
                  </div>

                  {isLimitedView ? (
                    <div className="flex flex-col gap-y-2 text-sm text-muted-foreground">
                      {partner.websiteUrl && (
                        <div>
                          <span className="font-medium">Website:</span>{' '}
                          <a
                            href={partner.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {getDomainName(partner.websiteUrl)}
                          </a>
                        </div>
                      )}
                      {partner.giveLink && (
                        <div>
                          <span className="font-medium">Giving Link:</span>{' '}
                          <a
                            href={partner.giveLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {partner.name}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-muted-foreground">
                        {association.contactPerson && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Regional Contact:</span> {association.contactPerson}
                          </div>
                        )}
                        {association.contactPhone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" /> {association.contactPhone}
                          </div>
                        )}
                        {association.contactEmail && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${association.contactEmail}`} className="text-primary hover:underline">
                              {association.contactEmail}
                            </a>
                          </div>
                        )}
                        {partner.contactPerson && (
                          <div>
                            <span className="font-medium">Main Contact:</span> {partner.contactPerson}
                          </div>
                        )}
                        {partner.phoneNumber && (
                          <div>
                            <span className="font-medium">Phone:</span> {partner.phoneNumber}
                          </div>
                        )}
                        {partner.email && (
                          <div>
                            <span className="font-medium">Email:</span> {partner.email}
                          </div>
                        )}
                        {partner.specialties && (
                          <div>
                            <span className="font-medium">Specialties:</span> {partner.specialties}
                          </div>
                        )}
                        {partner.address && (
                          <div className="md:col-span-2 lg:col-span-3">
                            <span className="font-medium">Address:</span> {partner.address}
                            {partner.city && `, ${partner.city}`}
                            {partner.state && `, ${partner.state}`}
                            {partner.zipCode && ` ${partner.zipCode}`}
                          </div>
                        )}
                        {partner.websiteUrl && (
                          <div>
                            <span className="font-medium">Website:</span>{' '}
                            <a
                              href={partner.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {getDomainName(partner.websiteUrl)}
                            </a>
                          </div>
                        )}
                        {partner.giveLink && (
                          <div>
                            <span className="font-medium">Giving Link:</span>{' '}
                            <a
                              href={partner.giveLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {partner.name}
                            </a>
                          </div>
                        )}
                      </div>

                      {partner.generalNotes && (
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Notes:</span> {partner.generalNotes}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {!isLimitedView && (
                  <div className="flex items-center justify-between">
                    {/* Status Toggle Switch - only visible to ADMIN and BASE_LEADER */}
                    {canManagePartners ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Status:
                        </span>
                        <StatusToggleSwitch
                          associationId={association.id}
                          currentStatus={association.isActive}
                          onStatusChange={handleToggleActive}
                        />
                        <span className="text-xs text-muted-foreground">
                          {association.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Status: {association.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    )}

                    {/* Edit and Delete buttons - only visible to ADMIN and BASE_LEADER */}
                    {canManagePartners && (
                      <div className="flex items-center gap-x-1">
                        <Button
                          variant="link"
                          size="icon"
                          onClick={() => handleEdit(partner.id)}
                          title="Edit"
                          className="w-7 h-7 bg-foreground rounded-md hover:bg-foreground/90 text-white font-bold"
                        >
                          <Edit className="w-4 text-white" />
                        </Button>
                        <Button
                          variant="link"
                          size="icon"
                          onClick={() => handleDelete(association.id)}
                          title="Remove from region"
                          className="w-7 h-7 bg-destructive rounded-md hover:bg-destructive/90 text-white font-bold"
                          disabled={deletingId === association.id}
                        >
                          <Trash2 className={`w-4 text-white ${deletingId === association.id ? 'animate-pulse' : ''}`} />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
